/** Live due/overdue labels for ticket queue cards. */

import { SYSTEM_STATUS_STAGES } from '@/lib/ticket-status-stages.js';

const DONE_STAGES = new Set(['closed', 'cancelled', 'ready']);
const DONE_SLUGS = new Set(['closed', 'cancelled', 'voided', 'ready', 'pending_pay', 'warranty']);

function isDoneStatus(status?: string) {
  if (!status) return true;
  if (DONE_SLUGS.has(status)) return true;
  const stage = SYSTEM_STATUS_STAGES[status];
  return stage != null && DONE_STAGES.has(stage);
}

export function isTicketOverdue(promisedDate?: string | null, status?: string) {
  if (!promisedDate || !status || isDoneStatus(status)) return false;
  return new Date(promisedDate).getTime() < Date.now();
}

export function formatOverdueAgo(promisedDate: string, now = Date.now()): string {
  const mins = Math.max(0, Math.floor((now - new Date(promisedDate).getTime()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m ago`;
  if (h > 0) return `${h}h ago`;
  return `${m}m ago`;
}

export function formatDueCountdown(
  promisedDate: string | null | undefined,
  status: string,
  now = Date.now(),
): { label: string; tone: 'overdue' | 'soon' | 'today' | 'later' | 'none' } {
  if (!promisedDate) return { label: 'No due time', tone: 'none' };
  if (isDoneStatus(status)) {
    return { label: `Due ${formatDueClock(promisedDate)}`, tone: 'none' };
  }

  const due = new Date(promisedDate).getTime();
  const diff = due - now;
  if (diff < 0) {
    const mins = Math.floor(-diff / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const ago = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
    return { label: `🔴 Overdue by ${ago}`, tone: 'overdue' };
  }

  const twoHours = 2 * 60 * 60 * 1000;
  if (diff <= twoHours) {
    const mins = Math.floor(diff / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const left = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
    return { label: `🟡 Due in ${left}`, tone: 'soon' };
  }

  const dueDate = new Date(promisedDate);
  const today = new Date(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const time = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
    dueDate,
  );

  if (dueDate.toDateString() === today.toDateString()) {
    return { label: `🟢 Due today at ${time}`, tone: 'today' };
  }
  if (dueDate.toDateString() === tomorrow.toDateString()) {
    return { label: `⚪ Due tomorrow at ${time}`, tone: 'later' };
  }
  return {
    label: `⚪ Due ${new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(dueDate)}`,
    tone: 'later',
  };
}

function formatDueClock(iso: string) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function useMinuteTick(): number {
  // Imported by callers that need a re-render each minute — implemented inline in page
  // to avoid extra hook file if unused. Kept here for shared helpers only.
  return Date.now();
}
